import { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  TablePagination,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import DashboardLayout from "@/components/DashboardLayout";
import * as patientApi from "@/api/patient";
import type { PatientResponseDTO } from "@/api/types";
import type { PatientFormData } from "@/types/PatientFormData";
import { dtoToFormData } from "@/utils/patientMapper";
import { PatientDisplay } from "./PatientEditModal";
import PatientEditModal2017 from "./PatientEditModal2017";
import PatientEditModal2018 from "./PatientEditModal2018";
import PatientEditModal2019 from "./PatientEditModal2019";
import PatientEditModal2020 from "./PatientEditModal2020";
import PatientEditModal2021 from "./PatientEditModal2021";
import PatientEditModal2022 from "./PatientEditModal2022";
import PatientEditModal2023 from "./PatientEditModal2023";
import PatientEditModal2024 from "./PatientEditModal2024";
import PatientEditModal2025 from "./PatientEditModal2025";
import PatientEditModal2026 from "./PatientEditModal2026";
import YearSelectionModal from "./YearSelectionModal";
import YearChangeModal from "./YearChangeModal";


// Helper function to get diagnosis label
function getDiagnosis(dto: any): string {
  if (dto.diagnoseHarninkontinenz || dto.DiagnoseHarninkontinenz) return "Harninkontinenz";
  if (dto.diagnoseSenkung || dto.DiagnoseSenkung) return "Senkung";
  if (dto.diagnoseKombination || dto.DiagnoseKombination) return "Kombination";
  return "";
}

// Helper function to get age group
function getAgeGroup(dto: any): string {
  if (dto.altersklassenKl30 || dto.AltersklassenKl30) return "< 30";
  if (dto.altersklassen3049 || dto.Altersklassen3049) return "30-49";
  if (dto.altersklassen5059 || dto.Altersklassen5059) return "50-59";
  if (dto.altersklassen6069 || dto.Altersklassen6069) return "60-69";
  if (dto.altersklassen7080 || dto.Altersklassen7080) return "70-80";
  if (dto.altersklassenGr80 || dto.AltersklassenGr80) return "> 80";
  return "";
}

// Helper function to get preop therapy
function getPreOpTherapy(dto: any): string {
  const therapies: string[] = [];
  if (dto.praeopKonsTherapieHormontherapie || dto.PraeopKonsTherapieHormontherapie) therapies.push("Hormontherapie");
  if (dto.praeopKonsTherapiePhysiotherapie || dto.PraeopKonsTherapiePhysiotherapie) therapies.push("Physiotherapie");
  return therapies.join(", ");
}

// Helper function to get surgery type
function getSurgeryType(dto: any): string {
  const types: string[] = [];
  if (dto.opTherapiePrimaereingriffHik || dto.OpTherapiePrimaereingriffHik) types.push("Prim. HIK");
  if (dto.opTherapiePrimaereingriffSenkung || dto.OpTherapiePrimaereingriffSenkung) types.push("Prim. Senkung");
  if (dto.opTherapieRezidiveingriffHik || dto.OpTherapieRezidiveingriffHik) types.push("Rezidiv HIK");
  if (dto.opTherapieRezidiveingriffSenkung || dto.OpTherapieRezidiveingriffSenkung) types.push("Rezidiv Senkung");
  if (dto.opTherapieWeitere || dto.OpTherapieWeitere) types.push("Weitere");
  return types.join(", ");
}

// Helper function to get op technique
function getOpTechnique(dto: any): string {
  const techniques: string[] = [];

  // Inkontinenz (ab 2023)
  if (dto.optechnikAb2023InkopTvtretro || dto.OptechnikAb2023InkopTvtretro) techniques.push("TVT retro.");
  if (dto.optechnikAb2023InkopTvtobtu || dto.OptechnikAb2023InkopTvtobtu) techniques.push("TVT obtu.");
  if (dto.optechnikAb2023InkopTvtmini || dto.OptechnikAb2023InkopTvtmini) techniques.push("TVT mini");
  if (dto.optechnikAb2023InkopBotox || dto.OptechnikAb2023InkopBotox) techniques.push("Botox");
  if (dto.optechnikAb2023InkopBulkingagent || dto.OptechnikAb2023InkopBulkingagent) techniques.push("Bulkingagent");
  if (dto.optechnikAb2023InkopOhne || dto.OptechnikAb2023InkopOhne) techniques.push("Inkop ohne");

  // Vaginale Senkung (ab 2023)
  if (dto.optechnikAb2023VagsenkVorderesnetzMitfixierung || dto.OptechnikAb2023VagsenkVorderesnetzMitfixierung) techniques.push("Vag. vord. Netz m. Fix.");
  if (dto.optechnikAb2023VagsenkVorderesnetzOhnefixierung || dto.OptechnikAb2023VagsenkVorderesnetzOhnefixierung) techniques.push("Vag. vord. Netz o. Fix.");
  if (dto.optechnikAb2023VagsenkHinteresnetz || dto.OptechnikAb2023VagsenkHinteresnetz) techniques.push("Vag. hint. Netz");
  if (dto.optechnikAb2023VagsenkKombiniertesnetz || dto.OptechnikAb2023VagsenkKombiniertesnetz) techniques.push("Vag. komb. Netz");
  if (dto.optechnikAb2023VagsenkApikalesband || dto.OptechnikAb2023VagsenkApikalesband) techniques.push("Vag. apik. Band");
  if (dto.optechnikAb2023VagsenkOhne || dto.OptechnikAb2023VagsenkOhne) techniques.push("Vag. senk. ohne");

  // Abdominale Senkung (ab 2023)
  if (dto.optechnikAb2023AbdsenkSacropexie || dto.OptechnikAb2023AbdsenkSacropexie) techniques.push("Abd. Sacropexie");
  if (dto.optechnikAb2023AbdsenkPectopecie || dto.OptechnikAb2023AbdsenkPectopecie) techniques.push("Abd. Pectopexie");
  if (dto.optechnikAb2023AbdsenkAndere || dto.OptechnikAb2023AbdsenkAndere) techniques.push("Abd. senk. andere");
  if (dto.optechnikAb2023AbdsenkOhne || dto.OptechnikAb2023AbdsenkOhne) techniques.push("Abd. senk. ohne");

  // Ab 2024
  if (dto.optechnikAb2024Burch || dto.OptechnikAb2024Burch) techniques.push("BURCH");
  if (dto.optechnikAb2024Weitere || dto.OptechnikAb2024Weitere) techniques.push("Weitere (2024)");

  // Ab 2025
  if (dto.optechnikAb2025Robotik || dto.OptechnikAb2025Robotik) techniques.push("Robotik");
  if (dto.optechnikAb2025MaterialId || dto.OptechnikAb2025MaterialId) techniques.push("Material-ID");

  // Vaginal Rekonstruktion
  if (dto.optechnikVagrekMit || dto.OptechnikVagrekMit) techniques.push("Vag. rek. mit");
  if (dto.optechnikVagrekMitVorderesnetz || dto.OptechnikVagrekMitVorderesnetz) techniques.push("Vag. rek. vord. Netz");
  if (dto.optechnikVagrekMitHinteresnetz || dto.OptechnikVagrekMitHinteresnetz) techniques.push("Vag. rek. hint. Netz");
  if (dto.optechnikVagrekMitKomplettesnetz || dto.OptechnikVagrekMitKomplettesnetz) techniques.push("Vag. rek. kompl. Netz");
  if (dto.optechnikVagrekMitTvt || dto.OptechnikVagrekMitTvt) techniques.push("Vag. rek. TVT");
  if (dto.optechnikVagrekMitTot || dto.OptechnikVagrekMitTot) techniques.push("Vag. rek. TOT");
  if (dto.optechnikVagrekMitMinischlinge || dto.OptechnikVagrekMitMinischlinge) techniques.push("Vag. rek. Minischlinge");
  if (dto.optechnikVagrekMitVagbilsak || dto.OptechnikVagrekMitVagbilsak) techniques.push("Vag. rek. Vagbilsak");
  if (dto.optechnikVagrekOhne || dto.OptechnikVagrekOhne) techniques.push("Vag. rek. ohne");

  // Laparoskopisch
  if (dto.optechnikLapMit || dto.OptechnikLapMit) techniques.push("Lap. mit");
  if (dto.optechnikLapMitVorderesnetz || dto.OptechnikLapMitVorderesnetz) techniques.push("Lap. vord. Netz");
  if (dto.optechnikLapMitHinteresnetz || dto.OptechnikLapMitHinteresnetz) techniques.push("Lap. hint. Netz");
  if (dto.optechnikLapMitKomplettesnetz || dto.OptechnikLapMitKomplettesnetz) techniques.push("Lap. kompl. Netz");
  if (dto.optechnikLapMitNetzsakro || dto.OptechnikLapMitNetzsakro) techniques.push("Lap. Netz sakro.");
  if (dto.optechnikLapMitNetzpecto || dto.OptechnikLapMitNetzpecto) techniques.push("Lap. Netz pecto.");
  if (dto.optechnikLapOhne || dto.OptechnikLapOhne) techniques.push("Lap. ohne");

  // Abdominale Rekonstruktion
  if (dto.optechnikAbdrekMit || dto.OptechnikAbdrekMit) techniques.push("Abd. rek. mit");
  if (dto.optechnikAbdrekMitVorderesnetz || dto.OptechnikAbdrekMitVorderesnetz) techniques.push("Abd. rek. vord. Netz");
  if (dto.optechnikAbdrekMitHinteresnetz || dto.OptechnikAbdrekMitHinteresnetz) techniques.push("Abd. rek. hint. Netz");
  if (dto.optechnikAbdrekMitKomplettesnetz || dto.OptechnikAbdrekMitKomplettesnetz) techniques.push("Abd. rek. kompl. Netz");
  if (dto.optechnikAbdrekMitNetzsakro || dto.OptechnikAbdrekMitNetzsakro) techniques.push("Abd. rek. Netz sakro.");
  if (dto.optechnikAbdrekMitNetzpecto || dto.OptechnikAbdrekMitNetzpecto) techniques.push("Abd. rek. Netz pecto.");
  if (dto.optechnikAbdrekOhne || dto.OptechnikAbdrekOhne) techniques.push("Abd. rek. ohne");

  // Sonstige
  if (dto.optechnikBotox || dto.OptechnikBotox) techniques.push("Botox (alt)");
  if (dto.optechnikBulking || dto.OptechnikBulking) techniques.push("Bulking (alt)");
  if (dto.optechnikKombination || dto.OptechnikKombination) techniques.push("Kombination");
  if (dto.optechnikRezidiv || dto.OptechnikRezidiv) techniques.push("Rezidiv");

  return techniques.length > 0 ? techniques.join(", ") : "-";
}

// Helper function to get complications
function getComplications(dto: any): string {
  const complications: string[] = [];
  if (dto.opkomplikationWundheilung || dto.OpkomplikationWundheilung) complications.push("Wundheilungsstörung");
  if (dto.opkomplikationNetzarrosion || dto.OpkomplikationNetzarrosion) complications.push("Netzarrosion");
  if (dto.opkomplikationSchmerzsyndrom || dto.OpkomplikationSchmerzsyndrom) complications.push("Schmerzsyndrom");
  if (dto.opkomplikationRevisionsop || dto.OpkomplikationRevisionsop) complications.push("Revisionsop");
  if (dto.opkomplikationOrganverletzung || dto.OpkomplikationOrganverletzung) complications.push("Organverletzung");
  if (dto.opkomplikationNahtdehiszenz || dto.OpkomplikationNahtdehiszenz) complications.push("Nahtdehiszenz");
  return complications.join(", ");
}

// Map backend DTO to display model
function mapPatientToDisplay(dto: any): PatientDisplay {
  // Backend returns camelCase, so access with lowercase first letter
  const id = dto.id || dto.Id;
  const jahr = dto.jahr || dto.Jahr;
  const patientenidentifikation = dto.patientenidentifikation || dto.Patientenidentifikation;

  return {
    id: id,
    year: jahr,
    patientId: patientenidentifikation,
    diagnosis: getDiagnosis(dto),
    ageGroup: getAgeGroup(dto),
    preOpTherapy: getPreOpTherapy(dto),
    surgeryType: getSurgeryType(dto),
    opTechnique: getOpTechnique(dto),
    complications: getComplications(dto),
    followUp3Months: dto.kontrolle3mErschienen || dto.Kontrolle3mErschienen ? "ja" : "nein",
    followUp12Months: dto.kontrolle12mErschienen || dto.Kontrolle12mErschienen ? "ja" : "nein",
    hasErrors: Boolean(dto.fehler || dto.Fehler),
    hasNotes: Boolean(dto.hinweise || dto.Hinweise),
  };
}

// Helper function: convert boolean to backend int format (1 or null)
const boolToInt = (value: boolean | undefined): number | null => (value ? 1 : null);

// Map form data to backend DTO format - Each field maps directly to database
function mapFormDataToDTO(formData: PatientFormData): any {
  // Get IK from logged-in user's organization
  const userIk = localStorage.getItem("userIk") || "";
  
  const dto: any = {
    Ik: userIk, // REQUIRED: Customer IK number
    Jahr: formData.year, // REQUIRED
    Patientenidentifikation: formData.patientId, // REQUIRED
  };

  // Age groups - direct mapping
  if (formData.altersklassen30_49) dto.Altersklassen3049 = 1;
  if (formData.altersklassen50_59) dto.Altersklassen5059 = 1;
  if (formData.altersklassen60_69) dto.Altersklassen6069 = 1;
  if (formData.altersklassen70_80) dto.Altersklassen7080 = 1;
  if (formData.altersklassenGr80) dto.AltersklassenGr80 = 1;
  if (formData.altersklassenKl30) dto.AltersklassenKl30 = 1;

  // Diagnosis - direct mapping
  if (formData.diagnoseHarninkontinenz) dto.DiagnoseHarninkontinenz = 1;
  if (formData.diagnoseKombination) dto.DiagnoseKombination = 1;
  if (formData.diagnoseSenkung) dto.DiagnoseSenkung = 1;

  // Preop therapy - direct mapping
  if (formData.praeopKonsTherapieHormontherapie) dto.PraeopKonsTherapieHormontherapie = 1;
  if (formData.praeopKonsTherapiePhysiotherapie) dto.PraeopKonsTherapiePhysiotherapie = 1;

  // OP Therapy - direct mapping
  if (formData.opTherapiePrimaereingriffHik) dto.OpTherapiePrimaereingriffHik = 1;
  if (formData.opTherapiePrimaereingriffSenkung) dto.OpTherapiePrimaereingriffSenkung = 1;
  if (formData.opTherapieRezidiveingriffHik) dto.OpTherapieRezidiveingriffHik = 1;
  if (formData.opTherapieRezidiveingriffSenkung) dto.OpTherapieRezidiveingriffSenkung = 1;
  if (formData.opTherapieWeitere) dto.OpTherapieWeitere = 1;

  // OP Technique - Inkontinenz (ab 2023)
  if (formData.optechnikAb2023InkopTvtretro) dto.OptechnikAb2023InkopTvtretro = 1;
  if (formData.optechnikAb2023InkopTvtobtu) dto.OptechnikAb2023InkopTvtobtu = 1;
  if (formData.optechnikAb2023InkopTvtmini) dto.OptechnikAb2023InkopTvtmini = 1;
  if (formData.optechnikAb2023InkopBotox) dto.OptechnikAb2023InkopBotox = 1;
  if (formData.optechnikAb2023InkopBulkingagent) dto.OptechnikAb2023InkopBulkingagent = 1;
  if (formData.optechnikAb2023InkopOhne) dto.OptechnikAb2023InkopOhne = 1;

  // OP Technique - Vaginal Senkung (ab 2023)
  if (formData.optechnikAb2023VagsenkVorderesnetzMitfixierung) dto.OptechnikAb2023VagsenkVorderesnetzMitfixierung = 1;
  if (formData.optechnikAb2023VagsenkVorderesnetzOhnefixierung) dto.OptechnikAb2023VagsenkVorderesnetzOhnefixierung = 1;
  if (formData.optechnikAb2023VagsenkHinteresnetz) dto.OptechnikAb2023VagsenkHinteresnetz = 1;
  if (formData.optechnikAb2023VagsenkKombiniertesnetz) dto.OptechnikAb2023VagsenkKombiniertesnetz = 1;
  if (formData.optechnikAb2023VagsenkApikalesband) dto.OptechnikAb2023VagsenkApikalesband = 1;
  if (formData.optechnikAb2023VagsenkOhne) dto.OptechnikAb2023VagsenkOhne = 1;

  // OP Technique - Abdominal Senkung (ab 2023)
  if (formData.optechnikAb2023AbdsenkSacropexie) dto.OptechnikAb2023AbdsenkSacropexie = 1;
  if (formData.optechnikAb2023AbdsenkPectopecie) dto.OptechnikAb2023AbdsenkPectopecie = 1;
  if (formData.optechnikAb2023AbdsenkAndere) dto.OptechnikAb2023AbdsenkAndere = 1;
  if (formData.optechnikAb2023AbdsenkOhne) dto.OptechnikAb2023AbdsenkOhne = 1;

  // OP Technique (ab 2024)
  if (formData.optechnikAb2024Burch) dto.OptechnikAb2024Burch = 1;
  if (formData.optechnikAb2024Weitere) dto.OptechnikAb2024Weitere = 1;

  // OP Technique (ab 2025)
  if (formData.optechnikAb2025Robotik) dto.OptechnikAb2025Robotik = 1;
  if (formData.optechnikAb2025MaterialId) dto.OptechnikAb2025MaterialId = formData.optechnikAb2025MaterialId;

  // OP Technique - Vaginal Reconstruction
  if (formData.optechnikVagrekMit) dto.OptechnikVagrekMit = 1;
  if (formData.optechnikVagrekMitVorderesnetz) dto.OptechnikVagrekMitVorderesnetz = 1;
  if (formData.optechnikVagrekMitHinteresnetz) dto.OptechnikVagrekMitHinteresnetz = 1;
  if (formData.optechnikVagrekMitKomplettesnetz) dto.OptechnikVagrekMitKomplettesnetz = 1;
  if (formData.optechnikVagrekMitTvt) dto.OptechnikVagrekMitTvt = 1;
  if (formData.optechnikVagrekMitTot) dto.OptechnikVagrekMitTot = 1;
  if (formData.optechnikVagrekMitMinischlinge) dto.OptechnikVagrekMitMinischlinge = 1;
  if (formData.optechnikVagrekMitVagbilsak) dto.OptechnikVagrekMitVagbilsak = 1;
  if (formData.optechnikVagrekOhne) dto.OptechnikVagrekOhne = 1;

  // OP Technique - Laparoscopic
  if (formData.optechnikLapMit) dto.OptechnikLapMit = 1;
  if (formData.optechnikLapMitVorderesnetz) dto.OptechnikLapMitVorderesnetz = 1;
  if (formData.optechnikLapMitHinteresnetz) dto.OptechnikLapMitHinteresnetz = 1;
  if (formData.optechnikLapMitKomplettesnetz) dto.OptechnikLapMitKomplettesnetz = 1;
  if (formData.optechnikLapMitNetzsakro) dto.OptechnikLapMitNetzsakro = 1;
  if (formData.optechnikLapMitNetzpecto) dto.OptechnikLapMitNetzpecto = 1;
  if (formData.optechnikLapOhne) dto.OptechnikLapOhne = 1;

  // OP Technique - Abdominal Reconstruction
  if (formData.optechnikAbdrekMit) dto.OptechnikAbdrekMit = 1;
  if (formData.optechnikAbdrekMitVorderesnetz) dto.OptechnikAbdrekMitVorderesnetz = 1;
  if (formData.optechnikAbdrekMitHinteresnetz) dto.OptechnikAbdrekMitHinteresnetz = 1;
  if (formData.optechnikAbdrekMitKomplettesnetz) dto.OptechnikAbdrekMitKomplettesnetz = 1;
  if (formData.optechnikAbdrekMitNetzsakro) dto.OptechnikAbdrekMitNetzsakro = 1;
  if (formData.optechnikAbdrekMitNetzpecto) dto.OptechnikAbdrekMitNetzpecto = 1;
  if (formData.optechnikAbdrekOhne) dto.OptechnikAbdrekOhne = 1;

  // OP Technique - Other
  if (formData.optechnikBotox) dto.OptechnikBotox = 1;
  if (formData.optechnikBulking) dto.OptechnikBulking = 1;
  if (formData.optechnikKombination) dto.OptechnikKombination = 1;
  if (formData.optechnikRezidiv) dto.OptechnikRezidiv = 1;

  // OP Complications
  if (formData.opkomplikationWundheilung) dto.OpkomplikationWundheilung = 1;
  if (formData.opkomplikationNetzarrosion) dto.OpkomplikationNetzarrosion = 1;
  if (formData.opkomplikationSchmerzsyndrom) dto.OpkomplikationSchmerzsyndrom = 1;
  if (formData.opkomplikationRevisionsop) dto.OpkomplikationRevisionsop = 1;
  if (formData.opkomplikationOrganverletzung) dto.OpkomplikationOrganverletzung = 1;
  if (formData.opkomplikationNahtdehiszenz) dto.OpkomplikationNahtdehiszenz = 1;

  // 3M Follow-up
  if (formData.kontrolle3mErschienen) dto.Kontrolle3mErschienen = 1;
  if (formData.kontrolle3mNichtmoeglichcorona) dto.Kontrolle3mNichtmoeglichcorona = 1;
  if (formData.kontrolle3mNeuerfalloderrezidiv) dto.Kontrolle3mNeuerfalloderrezidiv = 1;
  if (formData.kontrolle3mKontKontinent) dto.Kontrolle3mKontKontinent = 1;
  if (formData.kontrolle3mKontDeutlichgebessert) dto.Kontrolle3mKontDeutlichgebessert = 1;
  if (formData.kontrolle3mKontUnveraendert) dto.Kontrolle3mKontUnveraendert = 1;
  if (formData.kontrolle3mKontVerschlechtert) dto.Kontrolle3mKontVerschlechtert = 1;
  if (formData.kontrolle3mSenkungErreicht) dto.Kontrolle3mSenkungErreicht = 1;
  if (formData.kontrolle3mSenkungTeilweise) dto.Kontrolle3mSenkungTeilweise = 1;
  if (formData.kontrolle3mSenkungTeilrezidiv) dto.Kontrolle3mSenkungTeilrezidiv = 1;
  if (formData.kontrolle3mSenkungVollrezidiv) dto.Kontrolle3mSenkungVollrezidiv = 1;
  if (formData.kontrolle3mSekkompl_wundheilung) dto.Kontrolle3mSekkompl_wundheilung = 1;
  if (formData.kontrolle3mSekkompl_netzarrosion) dto.Kontrolle3mSekkompl_netzarrosion = 1;
  if (formData.kontrolle3mSekkompl_schmerzsyndrom) dto.Kontrolle3mSekkompl_schmerzsyndrom = 1;
  if (formData.kontrolle3mSekkompl_revisionsop) dto.Kontrolle3mSekkompl_revisionsop = 1;
  if (formData.kontrolle3mPatzufD21Ganz) dto.Kontrolle3mPatzufD21Ganz = 1;
  if (formData.kontrolle3mPatzufD21Teilweise) dto.Kontrolle3mPatzufD21Teilweise = 1;
  if (formData.kontrolle3mPatzufD21Garnicht) dto.Kontrolle3mPatzufD21Garnicht = 1;
  if (formData.kontrolle3mPatzufD22Ja) dto.Kontrolle3mPatzufD22Ja = 1;
  if (formData.kontrolle3mPatzufD22Nein) dto.Kontrolle3mPatzufD22Nein = 1;
  if (formData.kontrolle3mPatzufD23Ja) dto.Kontrolle3mPatzufD23Ja = 1;
  if (formData.kontrolle3mPatzufD23Nein) dto.Kontrolle3mPatzufD23Nein = 1;
  if (formData.kontrolle3mPatzufD23Weissnicht) dto.Kontrolle3mPatzufD23Weissnicht = 1;
  if (formData.kontrolle3mPatzufF1Ganz) dto.Kontrolle3mPatzufF1Ganz = 1;
  if (formData.kontrolle3mPatzufF1Teilweise) dto.Kontrolle3mPatzufF1Teilweise = 1;
  if (formData.kontrolle3mPatzufF1Kurzfristig) dto.Kontrolle3mPatzufF1Kurzfristig = 1;
  if (formData.kontrolle3mPatzufF1Garnicht) dto.Kontrolle3mPatzufF1Garnicht = 1;
  if (formData.kontrolle3mPatzufF2Nein) dto.Kontrolle3mPatzufF2Nein = 1;
  if (formData.kontrolle3mPatzufF2JaZiehen) dto.Kontrolle3mPatzufF2JaZiehen = 1;
  if (formData.kontrolle3mPatzufF2JaStechen) dto.Kontrolle3mPatzufF2JaStechen = 1;
  if (formData.kontrolle3mPatzufF2JaFremdkoerper) dto.Kontrolle3mPatzufF2JaFremdkoerper = 1;
  if (formData.kontrolle3mPatzufF2JaWeitere) dto.Kontrolle3mPatzufF2JaWeitere = 1;
  if (formData.kontrolle3mPatzufF2JaWeitereText) dto.Kontrolle3mPatzufF2JaWeitereText = formData.kontrolle3mPatzufF2JaWeitereText;
  if (formData.kontrolle3mPatzufF3Ganz) dto.Kontrolle3mPatzufF3Ganz = 1;
  if (formData.kontrolle3mPatzufF3Teils) dto.Kontrolle3mPatzufF3Teils = 1;
  if (formData.kontrolle3mPatzufF3Teilweise) dto.Kontrolle3mPatzufF3Teilweise = 1;
  if (formData.kontrolle3mPatzufF3Ehernicht) dto.Kontrolle3mPatzufF3Ehernicht = 1;
  if (formData.kontrolle3mPatzufF3Garnicht) dto.Kontrolle3mPatzufF3Garnicht = 1;

  // 12M Follow-up
  if (formData.kontrolle12mErschienen) dto.Kontrolle12mErschienen = 1;
  if (formData.kontrolle12mNichtmoeglichcorona) dto.Kontrolle12mNichtmoeglichcorona = 1;
  if (formData.kontrolle12mNeuerfalloderrezidiv) dto.Kontrolle12mNeuerfalloderrezidiv = 1;
  if (formData.kontrolle12mKontKontinent) dto.Kontrolle12mKontKontinent = 1;
  if (formData.kontrolle12mKontDeutlichgebessert) dto.Kontrolle12mKontDeutlichgebessert = 1;
  if (formData.kontrolle12mKontUnveraendert) dto.Kontrolle12mKontUnveraendert = 1;
  if (formData.kontrolle12mKontVerschlechtert) dto.Kontrolle12mKontVerschlechtert = 1;
  if (formData.kontrolle12mSenkungErreicht) dto.Kontrolle12mSenkungErreicht = 1;
  if (formData.kontrolle12mSenkungTeilweise) dto.Kontrolle12mSenkungTeilweise = 1;
  if (formData.kontrolle12mSenkungTeilrezidiv) dto.Kontrolle12mSenkungTeilrezidiv = 1;
  if (formData.kontrolle12mSenkungVollrezidiv) dto.Kontrolle12mSenkungVollrezidiv = 1;
  if (formData.kontrolle12mSekkompl_wundheilung) dto.Kontrolle12mSekkompl_wundheilung = 1;
  if (formData.kontrolle12mSekkompl_netzarrosion) dto.Kontrolle12mSekkompl_netzarrosion = 1;
  if (formData.kontrolle12mSekkompl_schmerzsyndrom) dto.Kontrolle12mSekkompl_schmerzsyndrom = 1;
  if (formData.kontrolle12mSekkompl_revisionsop) dto.Kontrolle12mSekkompl_revisionsop = 1;
  if (formData.kontrolle12mPatzufD21Ganz) dto.Kontrolle12mPatzufD21Ganz = 1;
  if (formData.kontrolle12mPatzufD21Teilweise) dto.Kontrolle12mPatzufD21Teilweise = 1;
  if (formData.kontrolle12mPatzufD21Garnicht) dto.Kontrolle12mPatzufD21Garnicht = 1;
  if (formData.kontrolle12mPatzufD22Ja) dto.Kontrolle12mPatzufD22Ja = 1;
  if (formData.kontrolle12mPatzufD22Nein) dto.Kontrolle12mPatzufD22Nein = 1;
  if (formData.kontrolle12mPatzufD23Ja) dto.Kontrolle12mPatzufD23Ja = 1;
  if (formData.kontrolle12mPatzufD23Nein) dto.Kontrolle12mPatzufD23Nein = 1;
  if (formData.kontrolle12mPatzufD23Weissnicht) dto.Kontrolle12mPatzufD23Weissnicht = 1;
  if (formData.kontrolle12mPatzufF1Ganz) dto.Kontrolle12mPatzufF1Ganz = 1;
  if (formData.kontrolle12mPatzufF1Teilweise) dto.Kontrolle12mPatzufF1Teilweise = 1;
  if (formData.kontrolle12mPatzufF1Kurzfristig) dto.Kontrolle12mPatzufF1Kurzfristig = 1;
  if (formData.kontrolle12mPatzufF1Garnicht) dto.Kontrolle12mPatzufF1Garnicht = 1;
  if (formData.kontrolle12mPatzufF2Nein) dto.Kontrolle12mPatzufF2Nein = 1;
  if (formData.kontrolle12mPatzufF2JaZiehen) dto.Kontrolle12mPatzufF2JaZiehen = 1;
  if (formData.kontrolle12mPatzufF2JaStechen) dto.Kontrolle12mPatzufF2JaStechen = 1;
  if (formData.kontrolle12mPatzufF2JaFremdkoerper) dto.Kontrolle12mPatzufF2JaFremdkoerper = 1;
  if (formData.kontrolle12mPatzufF2JaWeitere) dto.Kontrolle12mPatzufF2JaWeitere = 1;
  if (formData.kontrolle12mPatzufF2JaWeitereText) dto.Kontrolle12mPatzufF2JaWeitereText = formData.kontrolle12mPatzufF2JaWeitereText;
  if (formData.kontrolle12mPatzufF3Ganz) dto.Kontrolle12mPatzufF3Ganz = 1;
  if (formData.kontrolle12mPatzufF3Teils) dto.Kontrolle12mPatzufF3Teils = 1;
  if (formData.kontrolle12mPatzufF3Teilweise) dto.Kontrolle12mPatzufF3Teilweise = 1;
  if (formData.kontrolle12mPatzufF3Ehernicht) dto.Kontrolle12mPatzufF3Ehernicht = 1;
  if (formData.kontrolle12mPatzufF3Garnicht) dto.Kontrolle12mPatzufF3Garnicht = 1;

  // Status fields
  if (formData.fehler) dto.Fehler = 1;
  if (formData.hinweise) dto.Hinweise = 1;
  if (formData.vollstaendig) dto.Vollstaendig = 1;

  return dto;
}

const Patients = () => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState<PatientDisplay | null>(null);
  const [editingFormData, setEditingFormData] = useState<PatientFormData | null>(null);

  // Year selection modal state (for new patients)
  const [isYearSelectionOpen, setIsYearSelectionOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string>("");

  // Year change modal state (for existing patients)
  const [isYearChangeOpen, setIsYearChangeOpen] = useState(false);
  const [yearChangePatient, setYearChangePatient] = useState<PatientDisplay | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" | "warning" }>({ open: false, message: "", severity: "error" });

  const [patientIdFilter, setPatientIdFilter] = useState("");
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [tabFilter, setTabFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch patients from API
  const { data: patientsData, isLoading, error } = useQuery({
    queryKey: ["patients"],
    queryFn: patientApi.getAll,
  });

  // Map backend data to display model
  const patients = useMemo(() => {
    if (!patientsData) return [];
    return patientsData.map(mapPatientToDisplay);
  }, [patientsData]);

  // Filter patients
  const filteredPatients = useMemo(() => {
    return patients.filter((patient) => {
      const matchesPatientId = patient.patientId
        ?.toLowerCase()
        .includes(patientIdFilter.toLowerCase()) ?? true;
      const matchesYear =
        yearFilter === "all" || String(patient.year) === yearFilter;
      const matchesTab =
        tabFilter === "all" ||
        (tabFilter === "errors" && patient.hasErrors) ||
        (tabFilter === "notes" && patient.hasNotes);
      return matchesPatientId && matchesYear && matchesTab;
    });
  }, [patients, patientIdFilter, yearFilter, tabFilter]);

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: patientApi.remove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });

  const createMutation = useMutation({
    mutationFn: patientApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      setIsDialogOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      patientApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      setIsDialogOpen(false);
    },
  });

  const paginatedPatients = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPatients.slice(start, start + rowsPerPage);
  }, [filteredPatients, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddNew = () => {
    // First show year selection modal
    setIsYearSelectionOpen(true);
  };

  const handleYearSelected = (year: string) => {
    setSelectedYear(year);
    setIsYearSelectionOpen(false);
    // Now open the patient edit modal with the selected year
    setEditingPatient(null);
    setEditingFormData({ year } as PatientFormData);
    setIsDialogOpen(true);
  };

  const handleYearChangeClick = (patient: PatientDisplay) => {
    setYearChangePatient(patient);
    setIsYearChangeOpen(true);
  };

  const handleYearChange = async (newYear: string) => {
    if (!yearChangePatient) return;
    try {
      // Fetch full patient data
      const fullPatientData = await patientApi.getById(yearChangePatient.id);
      // Update year and save
      const backendData = { ...fullPatientData, Jahr: newYear };
      await updateMutation.mutateAsync({
        id: yearChangePatient.id,
        data: backendData,
      });
      setIsYearChangeOpen(false);
      setYearChangePatient(null);
    } catch (error: any) {
      console.error("Failed to change year:", error);
      setSnackbar({ open: true, message: error?.message || "Fehler beim Ändern des Jahres", severity: "error" });
    }
  };

  const handleEdit = async (patient: PatientDisplay) => {
    try {
      // Fetch full patient data from backend
      const fullPatientData = await patientApi.getById(patient.id);
      // Convert DTO to form data
      const formData = dtoToFormData(fullPatientData);
      
      setEditingPatient(patient);
      setEditingFormData(formData);
      setIsDialogOpen(true);
    } catch (error: any) {
      console.error("Failed to fetch patient data:", error);
      setSnackbar({ open: true, message: error?.message || "Fehler beim Laden der Patientendaten", severity: "error" });
    }
  };

  const handleDelete = async (id: number) => {
    if (confirm("Möchten Sie diese Patientin wirklich löschen?")) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error: any) {
        console.error("Failed to delete patient:", error);
        setSnackbar({ open: true, message: error?.message || "Fehler beim Löschen der Patientin", severity: "error" });
      }
    }
  };

  const handleSave = async (formData: PatientFormData) => {
    try {
      // Map frontend form data to backend DTO
      const backendData = mapFormDataToDTO(formData);
      
      if (editingPatient) {
        // Update existing patient
        await updateMutation.mutateAsync({
          id: editingPatient.id,
          data: backendData,
        });
        setSnackbar({ open: true, message: "Patientin erfolgreich aktualisiert", severity: "success" });
      } else {
        // Create new patient
        await createMutation.mutateAsync(backendData);
        setSnackbar({ open: true, message: "Patientin erfolgreich erstellt", severity: "success" });
      }
    } catch (error: any) {
      console.error("Failed to save patient:", error);
      setSnackbar({ open: true, message: error?.message || "Fehler beim Speichern der Patientin", severity: "error" });
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h4" component="h1" fontWeight="bold">
            Patientinnen
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            size="large"
            sx={{ color: "white" }}
          >
            Neue Patientin
          </Button>
        </Box>

        {/* Filter */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filter
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
            <TextField
              select
              label="Jahr"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">Alle Jahre</MenuItem>
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Patientenidentifikation"
              placeholder="ID eingeben..."
              value={patientIdFilter}
              onChange={(e) => setPatientIdFilter(e.target.value)}
              fullWidth
            />
          </Box>
        </Paper>

        {/* Tabs Filter */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={tabFilter}
            onChange={(_, newValue) => setTabFilter(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Alle" value="all" />
            <Tab label="Mit Fehlern" value="errors" />
            <Tab label="Mit Hinweisen" value="notes" />
          </Tabs>
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Fehler beim Laden der Patientinnen. Bitte versuchen Sie es erneut.
          </Alert>
        )}

        {/* Table */}
        {!isLoading && !error && (
          <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Jahr</strong></TableCell>
                  <TableCell><strong>Pat.-Ident.</strong></TableCell>
                  <TableCell><strong>Diagnose</strong></TableCell>
                  <TableCell><strong>AK</strong></TableCell>
                  <TableCell><strong>präop. kons. Therapie</strong></TableCell>
                  <TableCell><strong>Art des Eingriffs</strong></TableCell>
                  <TableCell><strong>OP Technik</strong></TableCell>
                  <TableCell><strong>Komplikationen</strong></TableCell>
                  <TableCell><strong>3 Monate</strong></TableCell>
                  <TableCell><strong>12 Monate</strong></TableCell>
                  <TableCell align="right"><strong>Aktionen</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPatients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        {patients.length === 0
                          ? "Keine Patientinnen vorhanden"
                          : "Keine Patientinnen entsprechen den Filterkriterien"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedPatients.map((patient) => (
                    <TableRow key={patient.id} hover>
                      <TableCell>{patient.year}</TableCell>
                      <TableCell>{patient.patientId}</TableCell>
                      <TableCell>{patient.diagnosis}</TableCell>
                      <TableCell>{patient.ageGroup}</TableCell>
                      <TableCell>{patient.preOpTherapy}</TableCell>
                      <TableCell>{patient.surgeryType}</TableCell>
                      <TableCell sx={{ maxWidth: 200, whiteSpace: "normal" }}>{patient.opTechnique}</TableCell>
                      <TableCell>{patient.complications}</TableCell>
                      <TableCell>{patient.followUp3Months}</TableCell>
                      <TableCell>{patient.followUp12Months}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(patient)}
                          color="primary"
                          title="Bearbeiten"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleYearChangeClick(patient)}
                          sx={{ color: "grey.700" }}
                          title="Jahr ändern"
                        >
                          <CalendarIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(patient.id)}
                          color="error"
                          title="Löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredPatients.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Zeilen pro Seite:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} von ${count}`
            }
          />
        </Paper>
        )}
      </Box>

      {/* Year Selection Modal (for new patients) */}
      <YearSelectionModal
        open={isYearSelectionOpen}
        onClose={() => setIsYearSelectionOpen(false)}
        onConfirm={handleYearSelected}
      />

      {/* Year Change Modal (for existing patients) */}
      <YearChangeModal
        open={isYearChangeOpen}
        onClose={() => {
          setIsYearChangeOpen(false);
          setYearChangePatient(null);
        }}
        onConfirm={handleYearChange}
        currentYear={yearChangePatient?.year || ""}
        patientId={yearChangePatient?.patientId || ""}
      />

      {/* Year-specific Edit Dialogs */}
      <PatientEditModal2017
        open={isDialogOpen && (editingFormData?.year === "2017" || editingPatient?.year === "2017")}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        editingPatient={editingPatient}
        editingFormData={editingFormData}
      />
      <PatientEditModal2018
        open={isDialogOpen && (editingFormData?.year === "2018" || editingPatient?.year === "2018")}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        editingPatient={editingPatient}
        editingFormData={editingFormData}
      />
      <PatientEditModal2019
        open={isDialogOpen && (editingFormData?.year === "2019" || editingPatient?.year === "2019")}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        editingPatient={editingPatient}
        editingFormData={editingFormData}
      />
      <PatientEditModal2020
        open={isDialogOpen && (editingFormData?.year === "2020" || editingPatient?.year === "2020")}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        editingPatient={editingPatient}
        editingFormData={editingFormData}
      />
      <PatientEditModal2021
        open={isDialogOpen && (editingFormData?.year === "2021" || editingPatient?.year === "2021")}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        editingPatient={editingPatient}
        editingFormData={editingFormData}
      />
      <PatientEditModal2022
        open={isDialogOpen && (editingFormData?.year === "2022" || editingPatient?.year === "2022")}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        editingPatient={editingPatient}
        editingFormData={editingFormData}
      />
      <PatientEditModal2023
        open={isDialogOpen && (editingFormData?.year === "2023" || editingPatient?.year === "2023")}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        editingPatient={editingPatient}
        editingFormData={editingFormData}
      />
      <PatientEditModal2024
        open={isDialogOpen && (editingFormData?.year === "2024" || editingPatient?.year === "2024")}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        editingPatient={editingPatient}
        editingFormData={editingFormData}
      />
      <PatientEditModal2025
        open={isDialogOpen && (editingFormData?.year === "2025" || editingPatient?.year === "2025")}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        editingPatient={editingPatient}
        editingFormData={editingFormData}
      />
      <PatientEditModal2026
        open={isDialogOpen && (editingFormData?.year === "2026" || editingPatient?.year === "2026")}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSave}
        editingPatient={editingPatient}
        editingFormData={editingFormData}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default Patients;
